import React from "react";
import PropTypes from "prop-types";
// Material imports
import makeStyles from "@mui/styles/makeStyles";
import Input from "@mui/material/Input";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { useTranslation } from "@limitless/common/hooks/translation/TranslationContext";

const useStyles = makeStyles(theme => {
    return {
        dropdownFormControl: {
            zIndex: "11000 !important"
        },
        dropdownContainer: {
            textAlign: props => (props.isRtl ? "right" : "left"),
            position: "relative",
            margin: "10px 0 4px 0",
            boxShadow: "0px 0px 1px -2px rgba(0, 0, 0, 0.20)"
        },
        dropdownContainerAfterSelection: {
            margin: "3px 0 8px 0"
        },
        dropdownInput: {
            padding: props => `0px ${props.isRtl ? "0px 10px" : "10px 0px"}`,
            display: "flex",
            height: "44px",
            alignItems: "center",
            border: "1px solid rgba(163, 168, 168, 0.50)",
            position: "relative",
            borderRadius: props => (props.showMenu ? "4px 4px 0px 0px" : "4px"),
            justifyContent: "space-between",
            userSelect: "none"
        },
        dropdownMenu: {
            position: "absolute",
            width: "100%",
            border: "1px solid rgba(163, 168, 168, 0.50)",
            borderRadius: "0px 0px 4px 4px",
            borderTop: "none",
            overflow: "auto",
            maxHeight: "120px",
            backgroundColor: "#fff",
            zIndex: 99,
            textAlign: props => (props.isRtl ? "right" : "left")
        },
        dropdownItem: {
            padding: "8px 16px",
            cursor: "pointer",
            "&:hover": {
                backgroundColor: theme.palette.dialogues.customer.background
            },
            "&:focus": {
                backgroundColor: theme.palette.dialogues.customer.background
            }
        },
        arrow: {
            border: "solid rgba(0, 0, 0, 0.54)",
            borderWidth: "0 2px 2px 0",
            display: "inline-block",
            padding: "3px"
        },
        up: {
            transform: "rotate(-135deg)"
        },
        down: {
            marginBottom: 3,
            transform: "rotate(45deg)"
        },
        line: {
            border: "solid rgba(163, 168, 168, 0.50)",
            borderWidth: props => (props.isRtl ? "0 0 0 1px" : "0 1px 0 0"),
            height: 35
        },
        iconContainer: {
            minWidth: "27px",
            display: "flex",
            alignItems: "center",
            justifyContent: props => (props.isRtl ? "flex-start" : "flex-end")
        },
        label: {
            textOverflow: "ellipsis",
            width: "98%"
        },
        helperTextLeftSpacing: {
            textAlign: props => (props.isRtl ? "right" : "left"),
            marginLeft: props => (props.isRtl ? 0 : 4),
            marginRight: props => (props.isRtl ? 4 : 0),
            direction: props => (props.isRtl ? "rtl" : "ltr")
        },
        icons: {
            margin: "auto",
            padding: "0 8px"
        }
    };
});

const InputDropdown = ({ placeHolder, options, onChange, errorMessage = "", value = "" }) => {
    const [showMenu, setShowMenu] = React.useState(false);
    const [selectedValue, setSelectedValue] = React.useState(value);
    const isRtl = document.documentElement.getAttribute("dir") === "rtl";

    const dropdownMenuRefs = React.useRef(Array(options.length));
    const inputRef = React.useRef();

    const classes = useStyles({ showMenu, isRtl });
    const { t } = useTranslation();

    React.useEffect(() => {
        const handler = e => {
            if (inputRef.current && !inputRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        };
        document.addEventListener("click", handler);

        return () => {
            document.removeEventListener("click", handler);
        };
    }, [inputRef]);

    React.useEffect(() => {
        if (showMenu) {
            dropdownMenuRefs.current[0].focus();
        }
    }, [showMenu]);

    const handleInputClick = () => {
        setShowMenu(!showMenu);
    };

    const onItemClick = option => {
        const newValue = option;

        setSelectedValue(newValue);
        onChange(newValue);
    };

    const handleKeyDown = event => {
        if (event.keyCode === 13 || event.keyCode === 32) {
            handleInputClick();
        }
    };

    const handleOptionKeyDown = (event, i) => {
        if (event.keyCode === 40 && i < options.length) {
            dropdownMenuRefs.current[i + 1]?.focus();
        } else if (event.keyCode === 38 && i < options.length) {
            dropdownMenuRefs.current[i - 1]?.focus();
        } else if (event.keyCode === 13) {
            onItemClick(options[i]);
            setShowMenu(!showMenu);
        }
    };

    const handleHover = i => {
        dropdownMenuRefs.current[i]?.focus();
    };

    const adjustedPlaceholder = isRtl ? `\u202A${t(placeHolder)}\u202C` : t(placeHolder);

    return (
        <FormControl className={classes.dropdownFormControl} error={Boolean(errorMessage)}>
            <div className={classes.dropdownContainer}>
                <div
                    ref={inputRef}
                    onClick={handleInputClick}
                    onKeyDown={handleKeyDown}
                    className={classes.dropdownInput}
                >
                    <Input
                        classes={{ input: classes.label }}
                        disableUnderline
                        fullWidth
                        value={selectedValue}
                        placeholder={adjustedPlaceholder}
                        inputProps={{
                            style: { textAlign: isRtl ? "right" : "left" },
                            dir: isRtl ? "rtl" : "ltr"
                        }}
                    />
                    <div className={classes.iconContainer}>
                        <i className={classes.line} />
                        <div className={classes.icons}>
                            {showMenu ? (
                                <i className={`${classes.arrow} ${classes.up}`} />
                            ) : (
                                <i className={`${classes.arrow} ${classes.down}`} />
                            )}
                        </div>
                    </div>
                </div>
                {showMenu && (
                    <div className={classes.dropdownMenu}>
                        {options.map((option, index) => (
                            <Typography
                                onClick={() => onItemClick(option)}
                                key={index}
                                ref={ele => (dropdownMenuRefs.current[index] = ele)}
                                onKeyDown={e => handleOptionKeyDown(e, index)}
                                onMouseOver={() => handleHover(index)}
                                className={classes.dropdownItem}
                                tabIndex={0}
                            >
                                {option}
                            </Typography>
                        ))}
                    </div>
                )}
                <FormHelperText component="div" className={classes.helperTextLeftSpacing}>
                    {t(errorMessage)}
                </FormHelperText>
            </div>
        </FormControl>
    );
};

InputDropdown.propTypes = {
    placeHolder: PropTypes.string,
    value: PropTypes.string,
    options: PropTypes.array,
    onChange: PropTypes.func,
    errorMessage: PropTypes.string
};

export default InputDropdown;
